// TODO create common interface
import { KalturaAssetListResponse } from '@kaltura-ott/ott-client-types/api/types';

import { KuxLinearType, KuxProgramType } from '../interfaces';

import { tvpilGetLocalCache } from './caching-service';
import { getConfig, KuXConfig } from './config';
import { EpgRequestArgs } from './epg-request';
import { startOfDay } from './utils/date';

export const CACHING_PREFIX = 'cache_';
let epgLimitCache: number | undefined;

export const setEpgLimitCache = (limit?: number) => (epgLimitCache = limit);

let partialHours = 24;

export const setEpgHoursPartial = (hours: number) => {
  partialHours = hours;
};

export const getEpgHoursPartial = () => {
  return partialHours;
};

let firstScreensChannels: number[] = [];

export const addToFirstScreen = (channelsId: number[]) => {
  firstScreensChannels = firstScreensChannels
    .concat(channelsId)
    .filter((item, index, self) => self.indexOf(item) == index);
  return firstScreensChannels;
};

/**
 * TODO DEPRECATED
 */
interface EpgCache {
  data: KuxProgramType[];
  time: number;
  id: number;
}

export let epgCache: { [key: string]: EpgCache } = {};

export const EPG_PREFIX = `${CACHING_PREFIX}channel_`;

/**
 * TODO DEPRECATED
 */
interface EpgCacheDays {
  cachedEpg: KuxProgramType[];
  cachedIdList: number[];
  noCachedIdList: number[];
}

export const getEpgCacheDays = async (data: EpgRequestArgs): Promise<EpgCacheDays> => {
  const completeIdList = data.linearIdList ?? [];
  const cache = tvpilGetLocalCache();
  const noCachedIdList: number[] = [];
  for (const channelId of completeIdList) {
    if (data.forceUpdate || !epgCache[EPG_PREFIX + data.startTime + '_' + channelId]) {
      const persistCache = (await cache.getItem(`${EPG_PREFIX}${data.startTime}_${channelId}`)) as EpgCache;
      if (persistCache) {
        epgCache[`${EPG_PREFIX}${data.startTime}_${channelId}`] = persistCache;
        continue;
      }
      if (channelId) {
        noCachedIdList.push(channelId);
      }
    }
  }

  const cachedIdList = completeIdList?.filter((channelId) => {
    return !data.forceUpdate && !channelId && epgCache[EPG_PREFIX + data.startTime + '_' + channelId];
  }) as number[];

  let cachedEpg: KuxProgramType[] = [];
  cachedIdList.forEach((channelId) => {
    cachedEpg = cachedEpg.concat(epgCache[EPG_PREFIX + data.startTime + '_' + channelId].data);
  });

  return { cachedEpg, cachedIdList, noCachedIdList };
};

export const checkEpgCacheLimit = () => {
  if (epgLimitCache && Object.keys(epgCache).length >= epgLimitCache) {
    const sortKeys = Object.keys(epgCache).sort((a, b) => epgCache[a].time - epgCache[b].time);
    for (const key of sortKeys) {
      const cache = epgCache[key];
      if (
        firstScreensChannels.includes(cache.id) &&
        cache.time > startOfDay() &&
        cache.time < startOfDay(new Date(), 2)
      ) {
        continue;
      }

      delete epgCache[key];

      if (Object.keys(epgCache).length < epgLimitCache) {
        return;
      }
    }
  }
};

export const clearEpgCacheDays = () => {
  epgCache = {};
};

export type CacheValue = {
  value: KalturaAssetListResponse;
  expiry: number;
};

export let channelsCache: { [key: string]: CacheValue } = {};
const CHANNEL_PREFIX = `${CACHING_PREFIX}tvchannel_`;

interface ChannelCache {
  channelId: number;
  kSql?: string;
  groupBy?: string;
}

export const getChannelCache = async (data: ChannelCache): Promise<KalturaAssetListResponse | undefined> => {
  const key = `${CHANNEL_PREFIX}${data.channelId}_${data.kSql}_${data.groupBy}`;
  const tempCache = channelsCache[key];
  let value;

  if (tempCache) {
    if (Date.now() > tempCache.expiry) {
      delete channelsCache[key];
    } else {
      value = tempCache.value;
    }
  }

  if (value) {
    return value;
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cache = (await tvpilGetLocalCache().getItem(key)) as any;
    return cache?.value;
  }
};

export const setChannelCache = (data: ChannelCache, value: KalturaAssetListResponse) => {
  channelsCache[`${CHANNEL_PREFIX}${data.channelId}_${data.kSql}_${data.groupBy}`] = {
    value,
    expiry: Date.now() + getConfig(KuXConfig)('persistCachingTtl'),
  };

  return value;
};

export const clearChannelCache = () => {
  channelsCache = {};
};

/* Cache for linear assets - Channels cache */
export const linearCache: { [key: string]: KuxLinearType } = {};
const LINEAR_PREFIX = `${CACHING_PREFIX}linear_`;

interface LinearCache {
  id: number;
}

export const getLinearCache = async (data: LinearCache): Promise<KuxLinearType> => {
  const cache = tvpilGetLocalCache();
  return (
    linearCache[`${LINEAR_PREFIX}${data.id}`] || ((await cache.getItem(`${LINEAR_PREFIX}${data.id}`)) as KuxLinearType)
  );
};

export const getLinearCacheList = async (data: LinearCache[]) => {
  const linears: KuxLinearType[] = [];
  for (let i = 0; i < data.length; i++) {
    const linear = await getLinearCache({ id: data[i].id });
    if (linear) {
      linears.push(linear);
    }
  }
  return linears;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setLinearCache = (data: LinearCache, value: any) => {
  return (linearCache[`${LINEAR_PREFIX}${data.id}`] = value);
};

/**
 * Sets cache items for local cache with ttl value
 *
 * Usage: Persist cache
 *
 * @category Epg Cache
 * @function
 */

export const cacheToPersist = async () => {
  const cache = tvpilGetLocalCache();
  try {
    const cachingData = {
      ...epgCache,
      ...channelsCache,
      ...linearCache,
    };

    const ttl = getConfig(KuXConfig)('persistCachingTtl');

    Object.keys(cachingData).forEach((cacheKey) => {
      cache.setItem(cacheKey, cachingData[cacheKey], ttl);
    });
  } catch (e) {
    // clear all storage
    cache.removeAllBySubPrefix(CACHING_PREFIX);
    throw e;
  }
};
